'use client';

import { useEffect, useRef } from 'react';
import { captureAttributionFromUrl, getStoredAttribution, syncAttributionToServer } from '@/lib/attribution';
import { getBrowserAccessToken } from '@/lib/supabase/session-client';

/**
 * Captures referral parameters and UTMs on landing.
 *
 * Essential functional tracker for creator affiliate attribution and fraud prevention.
 * Writes to a first-party cookie and localStorage on initial arrival.
 *
 * It also flushes stored attribution to the backend once a session exists. The
 * OAuth callback already persists attribution server-side, but email/password
 * sign-ups redirect straight to /dashboard and never pass through
 * /auth/callback - without this flush their referral would never be recorded
 * and the creator would go unpaid. `/v1/attribution` is idempotent per user and
 * preserves first-touch values, so a duplicate flush is harmless.
 */
let captured = false;

const SYNCED_STORAGE_KEY = 'purebox.attribution.synced.v1';

function hasAlreadySynced(): boolean {
  try {
    return window.sessionStorage.getItem(SYNCED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markSynced(): void {
  try {
    window.sessionStorage.setItem(SYNCED_STORAGE_KEY, '1');
  } catch {
    // Non-fatal: a repeat sync is idempotent server-side.
  }
}

export function AttributionTracker() {
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current || captured) {
      return;
    }
    hasRun.current = true;
    captured = true;

    captureAttributionFromUrl();

    void (async () => {
      try {
        if (hasAlreadySynced() || !getStoredAttribution()) {
          return;
        }

        const accessToken = await getBrowserAccessToken();
        if (!accessToken) {
          return;
        }

        const ok = await syncAttributionToServer(accessToken);
        if (ok) {
          markSynced();
        }
      } catch {
        // Attribution sync is best-effort and must never break rendering.
      }
    })();
  }, []);

  return null;
}
